import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { qk } from '../lib/query-keys';
import { fetchSettings, putSetting } from '../features/settings/api';

/** الاسم الافتراضي — وهو ما يُعرض قبل أن يحفظ المالك اسمًا. */
export const DEFAULT_BRAND_NAME = 'Flow';

/** مفتاح الإعداد؛ مُعلَنٌ في سجلّ الإعدادات بالخادم فلا يُقبل غيره. */
export const BRAND_NAME_KEY = 'brand.name';

/** حدُّ الطول — اسمٌ لا عنوان: ما لا يسع ترويسةً ضيّقة لا يُحفظ. */
export const BRAND_NAME_MAX = 24;

/**
 * **أعربيّ هو؟** — من المحارف لا من إعدادٍ ثانٍ.
 *
 * فالمالك يكتب «فلو» أو `Flow` ولا يُسأل عن اللغة. والمدى `؀-ۿ`
 * يغطّي العربية الأساسية، و`ݐ-ݿ` و`ﭐ-﻿` امتداداتها وأشكالها
 * المقدَّمة — فنصٌّ منسوخ من مكانٍ آخر لا يسقط إلى الخط اللاتيني.
 */
export function isArabicName(value: string): boolean {
  return /[؀-ۿݐ-ݿﭐ-﻿]/u.test(value);
}

/** الاسم بعد التنظيف — والفراغ يعني «أعِد الافتراضي» لا «احفظ فراغًا». */
export function normalizeBrandName(raw: string): string {
  const trimmed = raw.trim().replace(/\s+/gu, ' ').slice(0, BRAND_NAME_MAX);
  return trimmed === '' ? DEFAULT_BRAND_NAME : trimmed;
}

/**
 * اسمُ النظام في الترويسة — **يُحرَّر بنقرتين**.
 *
 * طلبه 2026-10-13: «كلمة Flow شيل الايقونة اللي جنبها، وخلي الكلمة ممكن
 * تغييرها من خلال الضغط مرتين، ولما اخليها بالعربي تصير بالخط IBM».
 *
 * - **ولا أيقونة**: `flow-mark` ذهبت. شعارٌ بجانب كلمةٍ يقول الشيء مرّتين،
 *   ومعيارُه المُعلن «كثرة الاشياء هذي تشتتني».
 * - **والخط يتبع المحارف**: عربيٌّ ⇒ `IBM Plex Sans Arabic` و`dir="rtl"`،
 *   ولاتينيٌّ ⇒ `Inter` و`dir="ltr"`. فلا إعداد لغةٍ ثانٍ يُضبط.
 * - **ويُحفظ في الخادم** لا في المتصفّح: الاسم يتبعه إلى الجوال.
 */
export function BrandName({ className = '' }: { readonly className?: string }) {
  const qc = useQueryClient();
  /*
   * **من قائمة الإعدادات نفسها** لا بنداءٍ ثانٍ لمفتاحٍ واحد: شاشة الإعدادات
   * تقرؤها بالمفتاح `qk.settings()`، فالاسم يشاركها الكاش — ولا رحلة إضافية
   * في كل فتح، ولا كاشان لقيمةٍ واحدة ينحرفان.
   */
  const saved = useQuery({ queryKey: qk.settings(), queryFn: fetchSettings });

  const row = saved.data?.find((item) => item.key === BRAND_NAME_KEY);
  const stored = typeof row?.value === 'string' ? row.value : '';
  const name = stored.trim() === '' ? DEFAULT_BRAND_NAME : stored;

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name);
  const field = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) field.current?.select();
  }, [editing]);

  const save = useMutation({
    mutationFn: (value: string) => putSetting(BRAND_NAME_KEY, value),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.settings() });
    },
  });

  const commit = (): void => {
    const next = normalizeBrandName(draft);
    setEditing(false);
    /* ولا نداء على ما لم يتغيّر: نقرتان بلا تعديل لا تكتبان سطر أثر. */
    if (next !== name) save.mutate(next);
  };

  const arabic = isArabicName(editing ? draft : name);
  const shared = `truncate text-heading ${arabic ? 'font-arabic' : ''}`;

  if (editing) {
    return (
      <input
        ref={field}
        value={draft}
        dir={arabic ? 'rtl' : 'ltr'}
        maxLength={BRAND_NAME_MAX}
        aria-label="اسم النظام"
        onChange={(event) => {
          setDraft(event.target.value);
        }}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') commit();
          /* والهروب يُلغي: ما كُتب بالخطأ لا يُحفظ لمجرّد أن المؤشّر غادر. */
          if (event.key === 'Escape') {
            setDraft(name);
            setEditing(false);
          }
        }}
        className={`${shared} ${className} min-w-0 max-w-[12ch] border-b border-accent bg-transparent text-center focus:outline-none`}
      />
    );
  }

  return (
    <span
      dir={arabic ? 'rtl' : 'ltr'}
      title="نقرتان لتغيير الاسم"
      onDoubleClick={() => {
        setDraft(name);
        setEditing(true);
      }}
      className={`${shared} ${className} bidi-isolate cursor-text select-none`}
    >
      {name}
    </span>
  );
}
